import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const DriverProfile = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [profilePictureUrl, setProfilePictureUrl] = useState(null);
  const [uploadingPicture, setUploadingPicture] = useState(false);
  const [picturePreview, setPicturePreview] = useState(null);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    vehicle_make: '',
    vehicle_model: '',
    vehicle_year: '',
    vehicle_color: '',
    max_passenger_capacity: '',
    bio: ''
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/drivers/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setProfile(response.data);
      setFormData({
        first_name: response.data.first_name,
        last_name: response.data.last_name,
        vehicle_make: response.data.vehicle_make || '',
        vehicle_model: response.data.vehicle_model || '',
        vehicle_year: response.data.vehicle_year || '',
        vehicle_color: response.data.vehicle_color || '',
        max_passenger_capacity: response.data.max_passenger_capacity || '',
        bio: response.data.bio || ''
      });
      // Set profile picture if exists
      if (response.data.profile_picture_url) {
        setProfilePictureUrl(response.data.profile_picture_url);
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      const detail = error.response?.data?.detail;
      let errorMsg = 'Failed to load profile';
      if (Array.isArray(detail)) {
        errorMsg = detail.map(err => err.msg || JSON.stringify(err)).join(', ');
      } else if (typeof detail === 'string') {
        errorMsg = detail;
      }
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleStripeOnboarding = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/drivers/stripe/onboarding`, {}, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error('Error starting Stripe onboarding:', error);
      alert('Failed to start Stripe onboarding');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const cleanedData = {
        ...formData,
        vehicle_year: formData.vehicle_year === '' ? null : Number(formData.vehicle_year),
        max_passenger_capacity: formData.max_passenger_capacity === '' ? null : Number(formData.max_passenger_capacity),
      };
      await axios.put(`${process.env.REACT_APP_API_URL}/api/drivers/profile`, cleanedData, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      alert('Profile updated successfully!');
      setEditing(false);
      fetchProfile();
    } catch (error) {
      console.error('Error updating profile:', error);
      const detail = error.response?.data?.detail;
      let message = 'Failed to update profile';
      if (Array.isArray(detail)) {
        message = detail.map(err => err.msg || JSON.stringify(err)).join(', ');
      } else if (typeof detail === 'string') {
        message = detail;
      }
      alert(message);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handlePictureSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError('Please upload a JPEG, PNG, or WebP image');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be less than 5MB');
      return;
    }

    // Show preview immediately
    const previewUrl = URL.createObjectURL(file);
    setPicturePreview(previewUrl);
    setError(null);

    // Upload to server
    setUploadingPicture(true);
    try {
      const token = localStorage.getItem('token');
      const uploadData = new FormData();
      uploadData.append('file', file);

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/drivers/profile-picture`,
        uploadData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      setProfilePictureUrl(response.data.profile_picture_url);
      setPicturePreview(null);
    } catch (error) {
      console.error('Error uploading picture:', error);
      const detail = error.response?.data?.detail;
      let uploadMsg = 'Failed to upload picture';
      if (Array.isArray(detail)) {
        uploadMsg = detail.map(err => err.msg || JSON.stringify(err)).join(', ');
      } else if (typeof detail === 'string') {
        uploadMsg = detail;
      }
      setError(uploadMsg);
      setPicturePreview(null);
    } finally {
      setUploadingPicture(false);
    }
  };

  const handleRemovePicture = async () => {
    setUploadingPicture(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/api/drivers/profile-picture`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setProfilePictureUrl(null);
      setPicturePreview(null);
    } catch (error) {
      console.error('Error removing picture:', error);
      setError('Failed to remove picture');
    } finally {
      setUploadingPicture(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center min-h-screen">Loading...</div>;
  if (error) return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="text-center">
        <p className="text-red-600 mb-4">{error}</p>
        <button onClick={fetchProfile} className="px-4 py-2 bg-blue-500 text-white rounded">
          Retry
        </button>
      </div>
    </div>
  );
  if (!profile) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate(-1)} className="text-gray-600 hover:text-gray-900">
              ← Back
            </button>
            <h1 className="text-2xl font-bold text-gray-900">Driver Profile</h1>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow p-6">
          {!editing ? (
            <div>
              {/* Profile Picture Section - View Mode */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-4">Profile Picture</h3>
                <div className="flex items-center gap-6">
                  <div className="relative">
                    {picturePreview || profilePictureUrl ? (
                      <img
                        src={picturePreview || profilePictureUrl}
                        alt="Profile"
                        className="w-24 h-24 rounded-full object-cover border-2 border-gray-200"
                      />
                    ) : (
                      <div className="w-24 h-24 rounded-full bg-gray-200 flex items-center justify-center">
                        <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                      </div>
                    )}
                    {uploadingPicture && (
                      <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 rounded-full">
                        <svg className="animate-spin h-8 w-8 text-blue-600" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="cursor-pointer px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-center text-sm font-medium">
                      {profilePictureUrl ? 'Change Photo' : 'Upload Photo'}
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        onChange={handlePictureSelect}
                        className="hidden"
                        disabled={uploadingPicture}
                      />
                    </label>
                    {profilePictureUrl && !profile?.onboarding_completed_at && (
                      <button
                        type="button"
                        onClick={handleRemovePicture}
                        disabled={uploadingPicture}
                        className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  JPEG, PNG, or WebP, max 5MB
                </p>
              </div>

              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-4">Personal Information</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">First Name</label>
                    <p className="mt-1">{profile.first_name}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Last Name</label>
                    <p className="mt-1">{profile.last_name}</p>
                  </div>
                </div>
              </div>

              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-4">Vehicle Information</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Make</label>
                    <p className="mt-1">{profile.vehicle_make || 'Not set'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Model</label>
                    <p className="mt-1">{profile.vehicle_model || 'Not set'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Year</label>
                    <p className="mt-1">{profile.vehicle_year || 'Not set'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Color</label>
                    <p className="mt-1">{profile.vehicle_color || 'Not set'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Max Passengers</label>
                    <p className="mt-1">{profile.max_passenger_capacity || 'Not set'}</p>
                  </div>
                </div>
              </div>

              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-4">Bio</h3>
                <p className="text-gray-700">{profile.bio || 'No bio added yet'}</p>
              </div>

              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-4">Statistics</h3>
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Total Rides</label>
                    <p className="mt-1 text-2xl font-bold text-green-600">{profile.total_rides || 0}</p>
                  </div>
                </div>
              </div>

              {/* Stripe Onboarding Status */}
              {!profile.stripe_connected_account_id && (
                <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <h3 className="text-lg font-semibold mb-2 text-yellow-800">Stripe Onboarding Required</h3>
                  <p className="text-sm text-yellow-700 mb-4">
                    Complete Stripe onboarding to receive payments for rides.
                  </p>
                  <button
                    onClick={handleStripeOnboarding}
                    className="px-6 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700"
                  >
                    Complete Stripe Onboarding
                  </button>
                </div>
              )}

              {profile.stripe_connected_account_id && !profile.stripe_onboarding_complete && (
                <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <h3 className="text-lg font-semibold mb-2 text-blue-800">Stripe Onboarding In Progress</h3>
                  <p className="text-sm text-blue-700 mb-4">
                    Your Stripe account is being set up. Complete the onboarding process to start receiving payments.
                  </p>
                  <button
                    onClick={handleStripeOnboarding}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    Continue Stripe Onboarding
                  </button>
                </div>
              )}

              {profile.stripe_connected_account_id && profile.stripe_onboarding_complete && (
                <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <h3 className="text-lg font-semibold mb-2 text-green-800">Stripe Onboarding Complete</h3>
                  <p className="text-sm text-green-700">
                    Your Stripe account is fully set up and ready to receive payments.
                  </p>
                </div>
              )}

              <button
                onClick={() => setEditing(true)}
                className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
              >
                Edit Profile
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              {/* Profile Picture Section - Edit Mode */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-4">Profile Picture</h3>
                <div className="flex items-center gap-6">
                  <div className="relative">
                    {picturePreview || profilePictureUrl ? (
                      <img
                        src={picturePreview || profilePictureUrl}
                        alt="Profile"
                        className="w-24 h-24 rounded-full object-cover border-2 border-gray-200"
                      />
                    ) : (
                      <div className="w-24 h-24 rounded-full bg-gray-200 flex items-center justify-center">
                        <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                      </div>
                    )}
                    {uploadingPicture && (
                      <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 rounded-full">
                        <svg className="animate-spin h-8 w-8 text-blue-600" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="cursor-pointer px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-center text-sm font-medium">
                      {profilePictureUrl ? 'Change Photo' : 'Upload Photo'}
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        onChange={handlePictureSelect}
                        className="hidden"
                        disabled={uploadingPicture}
                      />
                    </label>
                    {profilePictureUrl && !profile?.onboarding_completed_at && (
                      <button
                        type="button"
                        onClick={handleRemovePicture}
                        disabled={uploadingPicture}
                        className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  JPEG, PNG, or WebP, max 5MB
                </p>
              </div>

              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-4">Personal Information</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">First Name</label>
                    <input
                      type="text"
                      name="first_name"
                      value={formData.first_name}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Last Name</label>
                    <input
                      type="text"
                      name="last_name"
                      value={formData.last_name}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                </div>
              </div>

              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-4">Vehicle Information</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Make</label>
                    <input
                      type="text"
                      name="vehicle_make"
                      value={formData.vehicle_make}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      placeholder="Toyota"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Model</label>
                    <input
                      type="text"
                      name="vehicle_model"
                      value={formData.vehicle_model}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      placeholder="Camry"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Year</label>
                    <input
                      type="number"
                      name="vehicle_year"
                      value={formData.vehicle_year}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      placeholder="2020"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Color</label>
                    <input
                      type="text"
                      name="vehicle_color"
                      value={formData.vehicle_color}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      placeholder="Blue"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Max Passengers</label>
                    <input
                      type="number"
                      name="max_passenger_capacity"
                      value={formData.max_passenger_capacity}
                      onChange={handleChange}
                      min="1"
                      max="50"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      placeholder="4"
                    />
                  </div>
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Bio</label>
                <textarea
                  name="bio"
                  value={formData.bio}
                  onChange={handleChange}
                  rows="4"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="Tell riders about yourself..."
                />
              </div>

              <div className="flex gap-4">
                <button 
                  type="submit" 
                  className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                >
                  Save Changes
                </button>
                <button
                  type="button"
                  onClick={() => setEditing(false)}
                  className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>
      </main>
    </div>
  );
};

export default DriverProfile;